import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { postChat, getUsage } from "../lib/api";
import { Bot, User, Send, Sparkles, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import VoiceInput from "../components/VoiceInput";
import TtsButton from "../components/TtsButton";

function ensureSession() {
  let id = localStorage.getItem("intelvest_chat_session");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}`;
    localStorage.setItem("intelvest_chat_session", id);
  }
  return id;
}

export default function Assistant() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(ensureSession);
  const [usage, setUsage] = useState(null);
  const endRef = useRef(null);

  const refreshUsage = () => {
    getUsage().then(setUsage).catch(() => {});
  };

  // If launched from VoiceFab (?voice=1), auto-scroll to chat input + focus mic visually.
  // If launched with ?q=... (from VoiceCapture overlay), auto-send that as first message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("q");
    if (q && q.trim()) {
      // Clean URL so refresh doesn't re-send
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
      // Slight delay so component is mounted
      setTimeout(() => send(q.trim()), 200);
      return;
    }
    if (params.get("voice") === "1") {
      setTimeout(() => {
        const area = document.querySelector('[data-testid="chat-input-area"]');
        if (area) area.scrollIntoView({ behavior: "smooth", block: "center" });
        const micBtn = document.querySelector('[data-testid="voice-input-btn"]');
        if (micBtn) micBtn.classList.add("ring-2", "ring-gold-400", "ring-offset-2", "ring-offset-black");
        setTimeout(() => micBtn?.classList.remove("ring-2", "ring-gold-400", "ring-offset-2", "ring-offset-black"), 4000);
      }, 600);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messages.length === 0 && !loading) return;
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [messages, loading]);

  useEffect(() => { refreshUsage(); }, []);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const res = await postChat({ session_id: sessionId, message: content, lang });
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      refreshUsage();
    } catch (e) {
      // 402 = quota (global modal will pop). Only add an error bubble for real network/LLM failures.
      if (e?.response?.status === 402) {
        setMessages((m) => m.slice(0, -1));
        return;
      }
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            lang === "pt"
              ? "Desculpe, tive um problema para responder agora. Tente novamente em instantes."
              : "Sorry, I couldn't respond just now. Please try again shortly.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-ai">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
            <Sparkles size={10} className="inline mr-1" />
            Claude Sonnet 4.5
          </span>
          <h1 className="mt-2 font-display text-4xl lg:text-5xl tracking-tight">{t.ai.title}</h1>
          <p className="mt-3 text-neutral-400">{t.ai.sub}</p>
        </div>
        <UsageBadge usage={usage} lang={lang} />
      </div>

      <div className="mt-8 surface p-0 overflow-hidden" data-testid="chat-terminal">
        <div className="h-[520px] overflow-y-auto p-6 space-y-5" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="space-y-5">
              <div className="text-sm text-neutral-400">
                {lang === "pt"
                  ? "Comece perguntando algo como:"
                  : "Start by asking something like:"}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {t.ai.examples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => send(ex)}
                    className="text-left text-sm surface-elevated hover:border-gold-500/40 p-4 transition-colors"
                    data-testid={`example-prompt-${i}`}
                  >
                    <span className="font-mono text-xs text-gold-400 uppercase tracking-widest mr-1.5">
                      ›
                    </span>
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} content={m.content} />
          ))}
          {loading && (
            <div className="flex gap-3" data-testid="chat-loading">
              <Avatar role="assistant" />
              <div className="surface-elevated px-4 py-3 rounded-md inline-flex items-center gap-2 text-sm text-neutral-400">
                <span className="flex gap-1">
                  <Dot /> <Dot d="0.15s" /> <Dot d="0.3s" />
                </span>
                {t.common.thinking}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-white/10 p-3 bg-black/40" data-testid="chat-input-area">
          <div className="flex items-end gap-2">
            <VoiceInput
              disabled={loading}
              onText={(text) => send(text)}
            />
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={t.ai.placeholder}
              className="flex-1 bg-transparent text-sm resize-none focus:outline-none p-2 placeholder:text-neutral-500"
              data-testid="chat-input"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="bg-gold-500 hover:bg-gold-400 disabled:opacity-40 text-black px-4 py-2 rounded-md inline-flex items-center gap-1.5 transition-colors"
              data-testid="chat-send"
            >
              <Send size={14} />
              <span className="text-sm font-medium">{t.common.send}</span>
            </button>
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs text-neutral-600 font-mono uppercase tracking-[0.15em]">
        {lang === "pt"
          ? "Respostas geradas por IA baseadas em dados agregados. Não é aconselhamento financeiro."
          : "AI-generated responses based on aggregated data. Not financial advice."}
      </p>
    </div>
  );
}

const Bubble = ({ role, content }) => (
  <div className={`flex gap-3 ${role === "user" ? "flex-row-reverse" : ""}`} data-testid={`msg-${role}`}>
    <Avatar role={role} />
    <div
      className={`max-w-[80%] px-4 py-3 rounded-md text-sm leading-relaxed whitespace-pre-wrap relative group ${
        role === "user" ? "bg-gold-500/10 border border-gold-500/20 text-white" : "surface-elevated text-neutral-200"
      }`}
    >
      {content}
      {role === "assistant" && (
        <div className="mt-2 pt-2 border-t border-white/5 flex items-center gap-2">
          <TtsButton text={content} />
          <span className="text-[10px] uppercase tracking-widest text-neutral-600 font-mono">
            Claude Sonnet 4.5
          </span>
        </div>
      )}
    </div>
  </div>
);

const Avatar = ({ role }) => (
  <div
    className={`w-9 h-9 rounded-md grid place-items-center border ${
      role === "user" ? "bg-gold-500/10 border-gold-500/30 text-gold-400" : "bg-white/5 border-white/10 text-gold-400"
    } shrink-0`}
  >
    {role === "user" ? <User size={16} /> : <Bot size={16} />}
  </div>
);

const Dot = ({ d = "0s" }) => (
  <span
    className="w-1.5 h-1.5 rounded-full bg-gold-400 inline-block animate-pulse"
    style={{ animationDelay: d }}
  />
);

const UsageBadge = ({ usage, lang }) => {
  const pt = lang === "pt";
  if (!usage) return null;
  if (usage.unlimited) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gold-500/40 bg-gold-500/10 text-gold-300" data-testid="usage-badge-unlimited">
        <Crown size={14} />
        <span className="text-xs font-mono uppercase tracking-widest">
          {usage.plan === "elite" ? "Elite" : "Pro"} · {pt ? "ilimitado" : "unlimited"}
        </span>
      </div>
    );
  }
  const chat = usage.features?.chat || {};
  const remaining = chat.remaining ?? 0;
  const limit = chat.limit ?? 5;
  const low = remaining <= 1;
  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="usage-badge">
      <div className={`text-xs font-mono uppercase tracking-widest ${low ? "text-red-400" : "text-neutral-500"}`}>
        {pt ? "Plano gratuito" : "Free plan"} · {remaining}/{limit} {pt ? "hoje" : "today"}
      </div>
      <Link
        to="/planos"
        data-testid="upgrade-pill"
        className="inline-flex items-center gap-1.5 text-xs bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/40 text-gold-300 px-2.5 py-1 rounded-md transition-colors"
      >
        <Crown size={11} />
        {pt ? "Liberar ilimitado" : "Unlock unlimited"}
      </Link>
    </div>
  );
};
